import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader2, RefreshCw, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

/**
 * DailySummaryCard
 * Renders a per-day rollup table with click-to-expand agent drilldown.
 *
 * Props:
 *   endpoint      — API path, e.g. "/analytics/daily-summary" or "/ops/analytics/daily-summary"
 *   agentEndpoint — API path for per-agent breakdown (accepts date_from=date_to=date). Optional.
 *   params        — { team_id?, brand_id?, date_from?, date_to? }
 *   title         — Card heading
 *   subtitle      — Card subheading
 */
export default function DailySummaryCard({ endpoint, agentEndpoint, params, title, subtitle }) {
  const [data, setData] = useState({ days: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // date string
  const [agentRows, setAgentRows] = useState({}); // { [date]: [{...}] }
  const [agentLoading, setAgentLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(endpoint, { params })
      .then((r) => setData(r.data || { days: [] }))
      .catch(() => setData({ days: [] }))
      .finally(() => setLoading(false));
  }, [endpoint, params]);

  useEffect(() => { load(); }, [load]);

  const toggleRow = async (date) => {
    if (expanded === date) { setExpanded(null); return; }
    setExpanded(date);
    if (agentEndpoint && !agentRows[date]) {
      setAgentLoading(true);
      try {
        const r = await api.get(agentEndpoint, {
          params: { ...params, date_from: date, date_to: date },
        });
        setAgentRows((prev) => ({ ...prev, [date]: r.data || [] }));
      } catch {
        setAgentRows((prev) => ({ ...prev, [date]: [] }));
      } finally { setAgentLoading(false); }
    }
  };

  const days = data.days || [];
  const { rows: daysSorted, thProps } = useSortableTable(days, {
    initialKey: "date", initialDir: "desc",
    accessors: { date: (r) => r.date || "" },
  });

  return (
    <div className="glass-card p-4" data-testid="daily-summary-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="font-medium text-slate-900 font-outfit" data-testid="daily-summary-title">
            {title || "Daily summary"}
          </h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}
          data-testid="daily-summary-refresh" className="text-xs">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Hybrid bucketing legend — makes the date column's actual meaning
          impossible to misread. A call dialed on July 8 whose customer
          deposited on July 9 appears in the July 9 row (not July 8). */}
      <div
        className="text-[11px] text-slate-500 mb-2 px-2 py-1.5 rounded-md bg-slate-50/60 border border-slate-100"
        data-testid="daily-summary-bucketing-legend"
      >
        <span className="font-medium text-slate-600">Hybrid bucketing:</span>{" "}
        rows are grouped by <span className="font-medium">conversion date</span> for credited
        calls (the day the deposit landed) and by <span className="font-medium">call date</span>{" "}
        for uncredited calls. A call on Jul 8 whose customer deposited Jul 9 appears in the Jul 9 row.
      </div>

      {loading ? (
        <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : days.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm">
          No data for the selected filter.
        </div>
      ) : (
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm" data-testid="daily-summary-table">
            <thead data-sticky>
              <tr className="text-slate-500 text-[10px] uppercase">
                <SortableTh {...thProps("date", "left")} title="Conversion date for credited rows; call date otherwise. See legend above.">Bucket date</SortableTh>
                <SortableTh {...thProps("active_agents")}>Agents</SortableTh>
                <SortableTh {...thProps("total_calls")} title="Raw call rows bucketed to this date.">Rows</SortableTh>
                <SortableTh {...thProps("unique_users_called")} title="Unique users whose call rows landed on this date.">Users</SortableTh>
                <SortableTh {...thProps("fresh_calls")}>Fresh</SortableTh>
                <SortableTh {...thProps("followup_calls")}>Follow-ups</SortableTh>
                <SortableTh {...thProps("unique_users_contactable")}>Contactable</SortableTh>
                <SortableTh {...thProps("contactable_pct")} title="Contactable ÷ Users called.">Contact %</SortableTh>
                <SortableTh {...thProps("unique_users_converted")}>Converted</SortableTh>
                <SortableTh {...thProps("unique_users_total_converted")} className="bg-slate-50" title="Total across all credit channels.">Total conv</SortableTh>
                <SortableTh {...thProps("conversion_of_contactable_pct")} title="Converted ÷ Contactable.">Conv % (contactable)</SortableTh>
                <SortableTh {...thProps("unique_users_sms")} title="Users credited via SMS/voicemail.">SMS conv</SortableTh>
                <SortableTh {...thProps("unique_users_wn")} title="Users marked 'wrong number' who later deposited.">WN → Conv</SortableTh>
                <SortableTh {...thProps("unique_users_invalid")} title="Users marked 'invalid number' who later deposited.">Inv → Conv</SortableTh>
                <SortableTh {...thProps("conversion_pct")} title="Converted ÷ Users called — legacy.">Conv % (dialled)</SortableTh>
                <SortableTh {...thProps("deposits_after")}>Deposits after</SortableTh>
              </tr>
            </thead>
            <tbody>
              {daysSorted.map((d) => (
                <React.Fragment key={d.date}>
                  <tr
                    className={`border-t border-white/60 ${agentEndpoint ? "cursor-pointer" : ""} hover:bg-white/50`}
                    onClick={() => agentEndpoint && toggleRow(d.date)}
                    data-testid={`daily-summary-row-${d.date}`}
                  >
                    <td className="py-1.5 px-2 font-medium flex items-center gap-1">
                      {agentEndpoint && (
                        expanded === d.date
                          ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                          : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                      )}
                      {d.date}
                    </td>
                    <td className="py-1.5 px-2 text-right">{d.active_agents}</td>
                    <td className="py-1.5 px-2 text-right">{(d.total_calls || 0).toLocaleString()}</td>
                    <td className="py-1.5 px-2 text-right">{(d.unique_users_called || 0).toLocaleString()}</td>
                    <td className="py-1.5 px-2 text-right text-emerald-700">{(d.fresh_calls || 0).toLocaleString()}</td>
                    <td className="py-1.5 px-2 text-right text-blue-700">{(d.followup_calls || 0).toLocaleString()}</td>
                    <td className="py-1.5 px-2 text-right">{(d.unique_users_contactable || 0).toLocaleString()}</td>
                    <td className="py-1.5 px-2 text-right">{(d.contactable_pct ?? 0).toFixed(1)}%</td>
                    <td className="py-1.5 px-2 text-right font-semibold">{(d.unique_users_converted || 0).toLocaleString()}</td>
                    <td className="py-1.5 px-2 text-right bg-slate-50 font-semibold text-slate-900">
                      {(d.unique_users_total_converted || 0).toLocaleString()}
                      {d.unique_users_called > 0 && (
                        <span className="text-slate-400 text-[10px] ml-1">({(d.total_conversion_pct ?? 0).toFixed(1)}%)</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right font-semibold text-emerald-700">{(d.conversion_of_contactable_pct ?? 0).toFixed(1)}%</td>
                    <td className="py-1.5 px-2 text-right text-sky-700">
                      {(d.unique_users_sms || 0).toLocaleString()}
                      {d.unique_users_called > 0 && (
                        <span className="text-slate-400 text-[10px] ml-1">({(d.sms_conversion_pct ?? 0).toFixed(1)}%)</span>
                      )}
                    </td>
                    <td className={`py-1.5 px-2 text-right ${(d.unique_users_wn || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{(d.unique_users_wn || 0).toLocaleString()}</td>
                    <td className={`py-1.5 px-2 text-right ${(d.unique_users_invalid || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{(d.unique_users_invalid || 0).toLocaleString()}</td>
                    <td className="py-1.5 px-2 text-right text-slate-500">{(d.conversion_pct ?? 0).toFixed(1)}%</td>
                    <td className="py-1.5 px-2 text-right">{(d.deposits_after || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  </tr>

                  {agentEndpoint && expanded === d.date && (
                    <tr className="bg-slate-50/70">
                      <td colSpan={16} className="p-3">
                        {agentLoading && !agentRows[d.date] ? (
                          <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin" /></div>
                        ) : (agentRows[d.date] || []).length === 0 ? (
                          <div className="text-xs text-slate-500 text-center py-3">
                            No per-agent activity for {d.date}. (Ops uploads only — backfill has no agent info.)
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid={`agent-drilldown-${d.date}`}>
                              <thead>
                                <tr className="text-slate-500 text-[10px] uppercase">
                                  <th className="text-left py-1 px-2">Agent</th>
                                  <th className="text-right py-1 px-2">Calls</th>
                                  <th className="text-right py-1 px-2">Contactable</th>
                                  <th className="text-right py-1 px-2" title="Contactable ÷ Calls">Contact %</th>
                                  <th className="text-right py-1 px-2">Fresh</th>
                                  <th className="text-right py-1 px-2">Follow-ups</th>
                                  <th className="text-right py-1 px-2">Converted</th>
                                  <th className="text-right py-1 px-2 bg-slate-50" title="Total across all credit channels: Call + SMS + WN + Inv.">Total conv</th>
                                  <th className="text-right py-1 px-2" title="Converted ÷ Contactable">Conv % (contactable)</th>
                                  <th className="text-right py-1 px-2" title="Deposits attributed to SMS/voicemail nudge.">SMS conv</th>
                                  <th className="text-right py-1 px-2" title="Wrong-number users who later deposited.">WN → Conv</th>
                                  <th className="text-right py-1 px-2" title="Invalid-number users who later deposited.">Inv → Conv</th>
                                  <th className="text-right py-1 px-2" title="Converted ÷ Total calls (legacy)">Conv % (dialled)</th>
                                  <th className="text-right py-1 px-2" title="Percent of this agent's calls dispositioned as 'wrong number'. >30% is unusual — bad lead data.">Wrong number %</th>
                                  <th className="text-right py-1 px-2">Interested %</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(agentRows[d.date] || []).map((a) => (
                                  <tr key={a.agent_id || a.agent_name} className="border-t border-white/60">
                                    <td className="py-1 px-2 font-medium">{a.agent_name || a.agent_id || "—"}</td>
                                    <td className="py-1 px-2 text-right">{(a.total_calls || 0).toLocaleString()}</td>
                                    <td className="py-1 px-2 text-right">{(a.contactable || 0).toLocaleString()}</td>
                                    <td className="py-1 px-2 text-right">{(a.contactable_pct ?? 0).toFixed(1)}%</td>
                                    <td className="py-1 px-2 text-right text-emerald-700">{(a.fresh || 0).toLocaleString()}</td>
                                    <td className="py-1 px-2 text-right text-blue-700">{(a.followup || 0).toLocaleString()}</td>
                                    <td className="py-1 px-2 text-right font-semibold">{(a.converted || 0).toLocaleString()}</td>
                                    <td className="py-1 px-2 text-right bg-slate-50 font-semibold text-slate-900">
                                      {(a.total_converted || 0).toLocaleString()}
                                      {a.total_calls > 0 && (
                                        <span className="text-slate-400 text-[10px] ml-1">({(a.total_conversion_pct ?? 0).toFixed(1)}%)</span>
                                      )}
                                    </td>
                                    <td className="py-1 px-2 text-right font-semibold text-emerald-700">{(a.conversion_of_contactable_pct ?? 0).toFixed(1)}%</td>
                                    <td className="py-1 px-2 text-right text-sky-700">
                                      {(a.sms_converted || 0).toLocaleString()}
                                      {a.total_calls > 0 && (
                                        <span className="text-slate-400 text-[10px] ml-1">({(a.sms_conversion_pct ?? 0).toFixed(1)}%)</span>
                                      )}
                                    </td>
                                    <td className={`py-1 px-2 text-right ${(a.wn_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{(a.wn_converted || 0).toLocaleString()}</td>
                                    <td className={`py-1 px-2 text-right ${(a.invalid_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{(a.invalid_converted || 0).toLocaleString()}</td>
                                    <td className="py-1 px-2 text-right text-slate-500">{(a.conversion_pct ?? 0).toFixed(1)}%</td>
                                    <td className="py-1 px-2 text-right">{a.wrong_number_pct}%</td>
                                    <td className="py-1 px-2 text-right">{a.interested_pct}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
